import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Modal from '@components/modal/Modal'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import SimpleAlert from '@components/modal/SimpleAlert'
import ModalLoader from '@components/modal/ModalLoader'

import Layout from './AdminRoleLayout'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

import api from '@api/api'

import useWorkflow from './useWorkflow'
import { SCREENS } from './workflow'

interface Props {
  id?: string
  formStep?: AdminRoleStep
}

const init: Partial<AdminRoleForm> = {
  name: '',
  scopes: [],
}

const AdminUserFormScreen = ({ id, formStep }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const roles = useReducer(AdminStore.store, (s) => s.roles)

  const [form, setForm] = React.useState(init as AdminRoleForm)
  const [step, setStep] = React.useState(0)

  const [workflow] = useWorkflow(form, id ? 'scope' : undefined)

  React.useEffect(() => {
    if (id) {
      const role = roles.find((r) => r.id === id)

      if (role) {
        setForm({
          name: role.name,
          scopes: role.scopes,
          typeUtilisateur: role.typeUtilisateur,
        })
      }
    }
  }, [id])

  React.useEffect(() => {
    const idx = workflow.findIndex((w) => w === formStep)

    if (idx > -1) {
      setStep(idx)
    }
  }, [formStep, workflow])

  const onForm = (item: Partial<AdminRoleForm>) => {
    setForm({ ...form, ...item })
    return Promise.resolve()
  }

  const onNext = () => {
    if (step === workflow.length - 1) {
      onFinish()
    } else {
      const next = workflow[step + 1] || ''
      navigation.push(`/admin/roles/form/${next}`)
    }
  }

  const onBack = () => {
    if (id) {
      // no back for single change
      quit()
      return
    }

    if (step === 0) {
      navigation.goBack()
    } else {
      const previous = workflow[step - 1] || ''
      navigation.push(`/admin/roles/form/${previous}`)
    }
  }

  const quit = () => {
    if (id) {
      navigation.jumpTo(`/admin/roles/${id}`)
    } else {
      navigation.jumpTo(`/admin/roles`)
    }
  }

  const finish = () => {
    Modal.close()
    quit()
  }

  const reset = () => {
    Modal.close()
    setStep(0)
    setForm(init as AdminRoleForm)
  }

  const onFinish = () => {
    ModalLoader.open({ title: i18n.t('screens.admin.roles.form.sending') })

    if (id) {
      api.roles
        .update(id, { id, scopes: form.scopes })
        .then(() => {
          ModalLoader.close()
          finish()
        })
        .catch(() => {
          ModalLoader.close()

          SimpleAlert.open({
            title: i18n.t('common.error'),
            description: i18n.t('screens.admin.roles.form.sendingFailed'),
            buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
          })
        })
    } else {
      api.roles
        .create(form)
        .then(() => {
          ModalLoader.close()
          Modal.open({ Content: renderFinish, cancel: false })
        })
        .catch(() => {
          ModalLoader.close()

          SimpleAlert.open({
            title: i18n.t('common.error'),
            description: i18n.t('screens.admin.roles.form.sendingFailed'),
            buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
          })
        })
    }
  }

  const renderFinish = () => {
    return (
      <FinishContainer>
        <Title>{i18n.t('screens.admin.roles.form.sent')}</Title>
        <Info>{i18n.t('screens.admin.roles.form.sentInfo')}</Info>
        <Buttons>
          <ButtonContainer>
            <Button label={i18n.t('screens.admin.roles.form.title')} onClick={reset} color="light" status="active" />
          </ButtonContainer>
          <ButtonContainer>
            <Button label={i18n.t('common.finish')} onClick={finish} status="active" />
          </ButtonContainer>
        </Buttons>

        <Close activeOpacity={0.8} onPress={finish}>
          <Icon name="close" color={theme.colors.primary} size={25} />
        </Close>
      </FinishContainer>
    )
  }

  const screen = workflow[step]
  const FormScreen = SCREENS[screen]

  if (!screen) {
    return <Root left="close" onLeft={quit} header={i18n.t('screens.admin.roles.form.title')} fixed loading />
  }

  return (
    <Root left="close" onLeft={quit} header={i18n.t('screens.admin.roles.form.title')} fixed>
      <ItemContainer key={screen}>
        <Layout step={screen} form={form} onBack={onBack} onNext={onNext} onQuit={quit}>
          <FormScreen form={form} onForm={onForm} onBack={onBack} />
        </Layout>
      </ItemContainer>
    </Root>
  )
}

export default AdminUserFormScreen

const ItemContainer = styled(View)`
  flex: 1;
`

const FinishContainer = styled(View)`
  position: relative;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '24px 16px;' : '50px 140px')};
  max-width: 880px;
  background-color: ${(props) => props.theme.colors.background};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  right: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  padding: 15px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 24px;
  text-align: center;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const Buttons = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  margin-top: 38px;
`

const ButtonContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 270px;`}
  margin: 10px;
`
